import { TextBlockService, GridDefinitionService, UserGridConfigService } from "Service";
import { CssClass, TextBlockCategory, NotificationTypes, EncodingTypes } from "Enums";
import { Navigation, Util, Mapping, QueryParameter } from "Core";
import { IUserGridConfigRequest } from "Entities/IUserGridConfigRequest";
import { TooltipExtension } from "./TooltipExtension";
import { IBaseGrid } from "../Interfaces";
import { IDisposable } from "Core/Interfaces";
import { MappingDefinition, UserMappingConfigDefinition, UserGridConfigDefinition, GridTypes, Environment } from "Service/Client";

/**
 * Extension for Mapping to enable user grid configurations.
 * @hidden
 */
export class UserGridConfigExtension implements IDisposable {
    /**
     * @hidden
     */
    public static userGridConfigTemplate: string = require("../Templates/UserGridConfig.html").default;

    /**
     * @hidden
     */
    public static userGridConfigElementTemplate: string = require("../Templates/UserGridConfigElement.html").default;

    private defaultUserMappingConfig: UserMappingConfigDefinition;
    private currentUserMappingConfig: UserMappingConfigDefinition;

    private textBlockService: TextBlockService;
    private gridDefinitionService: GridDefinitionService;
    private userGridConfigService: UserGridConfigService;
    private userGridConfigViewModel: kendo.data.ObservableObject;

    private mapping: Mapping;
    private mappingDefinition: MappingDefinition;
    private allowSystemUserGridConfig: boolean;
    private toolbar: kendo.ui.ToolBar;

    /**
     * @hidden
     */
    protected tooltipExtension: TooltipExtension;

    /**
     * Create a new UserGridConfigExtension.
     * @param mapping
     * @param textBlockService
     * @param gridDefinitionService
     * @param userGridConfigService
     */
    constructor(
        mapping: Mapping,
        textBlockService: TextBlockService,
        gridDefinitionService: GridDefinitionService,
        userGridConfigService: UserGridConfigService,
        private environment: Environment
    ) {
        this.mapping = mapping;
        this.mappingDefinition = this.mapping.getMappingDefinition();
        this.allowSystemUserGridConfig = this.mappingDefinition.AllowSystemMappingGridConfig;
        this.textBlockService = textBlockService;
        this.gridDefinitionService = gridDefinitionService;
        this.userGridConfigService = userGridConfigService;
    }

    /**
     * Initialize and render the user grid configuration.
     */
    public async initialize(container: JQuery): Promise<void> {
        let userMappingConfigs = [...(this.mappingDefinition.UserMappingConfig || [])];
        let specificUserMappingConfigId = Number(QueryParameter.get(`${this.mappingDefinition.MappingName}` + "_DMS_UserMappingConfigID"));

        let userMappingConfigId = specificUserMappingConfigId
            ? specificUserMappingConfigId
            : Number(QueryParameter.get("DMS_UserMappingConfigID"));

        let selectedUserMappingConfig: UserMappingConfigDefinition;
        if (userMappingConfigId) {
            for (let i = 0, len = userMappingConfigs.length; i < len; i++) {
                if (userMappingConfigs[i].UserMappingConfigID == userMappingConfigId) {
                    this.currentUserMappingConfig = selectedUserMappingConfig = userMappingConfigs[i];
                    break;
                }
            }
        }

        for (const userMappingConfig of userMappingConfigs) {
            if (userMappingConfig.IsInitial) {
                this.currentUserMappingConfig = selectedUserMappingConfig = userMappingConfig;
                break;
            }
        }

        await this.textBlockService.load(this.environment.LanguageCode, TextBlockCategory.userGridConfig);

        let content = kendo.template(UserGridConfigExtension.userGridConfigTemplate, { useWithBlock: false })({
            Grid: this,
        });
        if (!this.defaultUserMappingConfig) {
            this.defaultUserMappingConfig = {
                UserMappingConfigID: null,
                Name: this.textBlockService.get("DefaultViewName", TextBlockCategory.userGridConfig),
                IsSystem: true,
            };
        }
        userMappingConfigs.unshift(this.defaultUserMappingConfig);

        const wrapper = $(content);
        container.prepend(wrapper);
        this.userGridConfigViewModel = kendo.observable({
            userGridConfigs: userMappingConfigs,
            selectedUserGridConfig: selectedUserMappingConfig || this.defaultUserMappingConfig,
            allowSystemGridConfig: this.allowSystemUserGridConfig,
            isDeleteEnabled: function () {
                let config = this.get("selectedUserGridConfig") as UserMappingConfigDefinition;
                return (
                    config != null &&
                    config.UserMappingConfigID != null &&
                    (!config.IsSystem || (config.IsSystem && this.get("allowSystemGridConfig")))
                );
            },
        });
        this.userGridConfigViewModel.bind("change", (e: any) => {
            if (e.field == "selectedUserGridConfig") {
                this.currentUserMappingConfig = this.userGridConfigViewModel.get("selectedUserGridConfig");
                this.applyUserMappingConfig();
            }
        });

        let helpText = this.textBlockService.get("HelpText", TextBlockCategory.userGridConfig);
        let helpGridTypes: number[] = [];
        for (let i = 0, len = this.mappingDefinition.GridDefinitions.length; i < len; i++) {
            let gridDefinition = this.mappingDefinition.GridDefinitions[i];
            if (gridDefinition.CanUserGridConfig && helpGridTypes.indexOf(gridDefinition.GridTypeID) == -1) {
                helpGridTypes.push(gridDefinition.GridTypeID);
                helpText +=
                    "<br />" +
                    this.textBlockService.get("HelpText_" + GridTypes[gridDefinition.GridTypeID], TextBlockCategory.userGridConfig);
            }
        }
        helpText = Util.encode(helpText, EncodingTypes.HtmlAttribute);

        let toolbarContainer = $("<div>").appendTo(container.find("." + CssClass.userGridConfig));
        try {
            this.toolbar = new kendo.ui.ToolBar(toolbarContainer[0], {
                resizable: false,
                items: [
                    { template: `<label>${this.textBlockService.get("ToolbarTitle", TextBlockCategory.userGridConfig)}</label>` },
                    { template: `<span class="dms-helptext far fa-question-circle" data-dms-helptext="${helpText}"></span>` },
                    { template: UserGridConfigExtension.userGridConfigElementTemplate },
                    {
                        type: "button",
                        overflow: "never",
                        attributes: {
                            class: "dms-tooltip dms-usergridconfig-save",
                            "data-dms-tooltip": this.textBlockService.get("SaveButtonToolTip", TextBlockCategory.userGridConfig),
                        },
                        spriteCssClass: "far fa-save",
                        click: () => this.onUserConfigSaveButtonClick(),
                    },
                    {
                        type: "button",
                        overflow: "never",
                        attributes: {
                            class: "dms-tooltip dms-usergridconfig-initial",
                            "data-dms-tooltip": this.textBlockService.get("InitialButtonToolTip", TextBlockCategory.userGridConfig),
                        },
                        spriteCssClass: "far fa-star",
                        click: () => this.onUserConfigInitialButtonClick(),
                    },
                    {
                        type: "button",
                        overflow: "never",
                        attributes: {
                            class: "dms-tooltip dms-usergridconfig-delete",
                            "data-dms-tooltip": this.textBlockService.get("DeleteButtonToolTip", TextBlockCategory.userGridConfig),
                            "data-bind": "enabled: isDeleteEnabled()",
                        },
                        spriteCssClass: "far fa-trash-alt",
                        click: () => this.onUserConfigDeleteButtonClick(),
                    },
                ],
            });
        } catch {
            // nothing to do, kendo bug: error on initialization
        }
        kendo.bind(wrapper, this.userGridConfigViewModel);

        if (!this.tooltipExtension) {
            this.tooltipExtension = new TooltipExtension();
        }
        this.tooltipExtension.initialize(toolbarContainer);
    }

    /**
     * Occurs when the save button in the toolbar was clicked.
     */
    private async onUserConfigSaveButtonClick(): Promise<void> {
        let isOverwrite = false;

        // overwrite when current config is not the default config..
        if (this.currentUserMappingConfig && this.currentUserMappingConfig.UserMappingConfigID) {
            // .. and the userconfig is either non system or system and the user has the permission to write system
            isOverwrite =
                !this.currentUserMappingConfig.IsSystem || (this.currentUserMappingConfig.IsSystem && this.allowSystemUserGridConfig);
        }

        let configurationId: number = null;
        if (isOverwrite) {
            let overwriteResult = await Navigation.confirmAsync(
                this.textBlockService
                    .get("ConfirmOverwriteText", TextBlockCategory.userGridConfig)
                    .split("@@name@@")
                    .join(this.currentUserMappingConfig.Name),
                this.textBlockService.get("ConfirmOverwriteTitle", TextBlockCategory.userGridConfig)
            );
            if (overwriteResult) {
                this.saveUserGridConfig(
                    this.currentUserMappingConfig.UserMappingConfigID,
                    this.currentUserMappingConfig.IsSystem,
                    this.currentUserMappingConfig.Name
                );
                return;
            }
        }

        let dialogActions: kendo.ui.DialogAction[] = [];
        dialogActions.push({
            text: this.textBlockService.get("ConfigDialogSaveButtonText", TextBlockCategory.userGridConfig),
            primary: true,
            action: (e: kendo.ui.DialogEvent) => {
                this.userGridConfigDialogCheckSave(e, false, configurationId);
            },
        });
        if (this.allowSystemUserGridConfig) {
            dialogActions.push({
                text: this.textBlockService.get("ConfigDialogSaveSystemButtonText", TextBlockCategory.userGridConfig),
                action: (e: kendo.ui.DialogEvent) => {
                    this.userGridConfigDialogCheckSave(e, true, configurationId);
                },
            });
        }
        dialogActions.push({
            text: this.textBlockService.get("DialogCancelText", TextBlockCategory.userGridConfig),
        });

        let dialog = new kendo.ui.Dialog($("<div>")[0], {
            width: "480px",
            modal: true,
            content:
                this.textBlockService.get("ConfigDialogText", TextBlockCategory.userGridConfig) +
                `<br/><br/><input type="text" class="k-textbox dms-usergridconfig-text" />`,
            title: this.textBlockService.get("ConfigDialogTitle", TextBlockCategory.userGridConfig),
            actions: dialogActions,
            visible: false,
            close: (e) => {
                e.sender.destroy();
            },
        });
        dialog.open();
        dialog.element.find(".k-textbox").focus();
    }

    /**
     * Get user grid configuration for specific grid.
     * @param gridId Id of the grid.
     * @hidden
     */
    public getUserGridConfig(gridId: number): UserGridConfigDefinition {
        if (this.currentUserMappingConfig && this.currentUserMappingConfig.UserGridConfig) {
            for (let i = 0, len = this.currentUserMappingConfig.UserGridConfig.length; i < len; i++) {
                let gridConfig = this.currentUserMappingConfig.UserGridConfig[i];

                if (gridConfig.GridID == gridId) {
                    return gridConfig;
                }
            }
        }
    }

    /**
     * Apply the current config to the mapping.
     */
    private async applyUserMappingConfig(): Promise<void> {
        let grids = this.mapping.getGrids();

        let loadGrids: IBaseGrid[] = [];

        for (const grid of grids) {
            if (!grid.isUserGridConfigEnabled) {
                continue;
            }
            let hasConfig = false;
            if (this.currentUserMappingConfig && this.currentUserMappingConfig.UserGridConfig) {
                for (const gridConfig of this.currentUserMappingConfig.UserGridConfig) {
                    if (gridConfig.GridID == grid.getGridId()) {
                        await grid.applyUserGridConfig(gridConfig);
                        hasConfig = true;
                        break;
                    }
                }
            }
            if (!hasConfig) {
                await grid.applyUserGridConfig(null);
            }
            loadGrids.push(grid);
        }
        this.mapping.initialLoad(loadGrids);
    }

    /**
     * Checks if text was entered and saves the user grid config.
     * @param e
     * @param isSystem
     * @param userMappingConfigId
     */
    private userGridConfigDialogCheckSave(e: kendo.ui.DialogEvent, isSystem: boolean, userMappingConfigId: number) {
        let text = e.sender.element.find(".k-textbox").val() as string;
        if (Util.stringIsNullOrEmpty(text)) {
            Navigation.errorAsync(
                this.textBlockService.get("ConfigDialogEmptyText", TextBlockCategory.userGridConfig),
                this.textBlockService.get("ConfigDialogEmptyText", TextBlockCategory.userGridConfig)
            );
            return false;
        }
        this.saveUserGridConfig(userMappingConfigId, isSystem, text);
    }

    /**
     * Save the user grid config and reload the list.
     * @param userMappingConfigId
     * @param isSystem
     * @param name
     */
    private async saveUserGridConfig(userMappingConfigId: number, isSystem: boolean, name: string): Promise<void> {
        this.mapping.setBusy(true);

        let userGridConfig: IUserGridConfigRequest[] = [];
        for (let i = 0, len = this.mappingDefinition.GridDefinitions.length; i < len; i++) {
            let gridDefinition = this.mappingDefinition.GridDefinitions[i];
            if (gridDefinition.CanUserGridConfig) {
                let grid = this.mapping.getGrid(gridDefinition.GridID);
                if (grid) {
                    let configuration = grid.createUserGridConfig();

                    let userGridConfigId: number = null;
                    if (userMappingConfigId !== null && this.currentUserMappingConfig && this.currentUserMappingConfig.UserGridConfig) {
                        for (let j = 0, configLen = this.currentUserMappingConfig.UserGridConfig.length; j < configLen; j++) {
                            let userGridConfig = this.currentUserMappingConfig.UserGridConfig[j];
                            if (userGridConfig && userGridConfig.GridID == gridDefinition.GridID) {
                                userGridConfigId = userGridConfig.UserGridConfigID;
                                break;
                            }
                        }
                    }

                    userGridConfig.push({
                        Configuration: configuration,
                        GridID: gridDefinition.GridID,
                        UserGridConfigID: userGridConfigId,
                    });
                }
            }
        }

        let mappingConfig = await this.userGridConfigService.setConfig(
            this.mappingDefinition.MappingName,
            isSystem,
            userMappingConfigId,
            name,
            userGridConfig
        );

        await this.reload(mappingConfig.UserMappingConfigID);
        this.mapping.setBusy(false);
        Navigation.notify(
            this.textBlockService.get("NotificationSaveSuccess", TextBlockCategory.userGridConfig),
            NotificationTypes.Success
        );
    }

    /**
     * Occurs when the initial button is clicked.
     */
    private async onUserConfigInitialButtonClick(): Promise<void> {
        let userMappingConfigId = this.currentUserMappingConfig ? this.currentUserMappingConfig.UserMappingConfigID : undefined;

        this.mapping.setBusy(true);
        await this.userGridConfigService.setInitial(this.mappingDefinition.MappingName, userMappingConfigId);
        this.mapping.setBusy(false);
        Navigation.notify(
            this.textBlockService.get("NotificationInitialSuccess", TextBlockCategory.userGridConfig),
            NotificationTypes.Success
        );
    }

    /**
     * Occurs when the delete button is clicked.
     */
    private async onUserConfigDeleteButtonClick(): Promise<void> {
        if (!this.currentUserMappingConfig || !this.userGridConfigViewModel.get("isDeleteEnabled()")) {
            return;
        }
        let deleteResult = await Navigation.confirmAsync(
            this.textBlockService
                .get("DeleteConfirmText", TextBlockCategory.userGridConfig)
                .split("@@name@@")
                .join(this.currentUserMappingConfig.Name),
            this.textBlockService.get("DeleteConfirmTitle", TextBlockCategory.userGridConfig)
        );

        if (deleteResult) {
            this.mapping.setBusy(true);
            await this.userGridConfigService.deleteConfig(
                this.mappingDefinition.MappingName,
                this.currentUserMappingConfig.UserMappingConfigID
            );

            await this.reload();
            this.mapping.setBusy(false);
            Navigation.notify(
                this.textBlockService.get("NotificationDeleteSuccess", TextBlockCategory.userGridConfig),
                NotificationTypes.Success
            );
        }
    }

    /**
     * Reload the user mapping dropdown.
     * @param selectedUserMappingConfigId The id of the config which should be selected after reloading. Leave empty if the standard should be selected.
     */
    private async reload(selectedUserMappingConfigId: number = null): Promise<void> {
        let mappingDefinition = await this.gridDefinitionService.getMapping(this.mappingDefinition.MappingName);
        this.mappingDefinition.UserMappingConfig = mappingDefinition.UserMappingConfig;

        let userMappingConfigs = [...(this.mappingDefinition.UserMappingConfig || [])];
        userMappingConfigs.unshift(this.defaultUserMappingConfig);
        this.userGridConfigViewModel.set("userGridConfigs", userMappingConfigs);

        if (selectedUserMappingConfigId) {
            for (let i = 0, len = userMappingConfigs.length; i < len; i++) {
                if (userMappingConfigs[i].UserMappingConfigID == selectedUserMappingConfigId) {
                    this.userGridConfigViewModel.set("selectedUserGridConfig", userMappingConfigs[i]);
                    break;
                }
            }
        } else {
            this.userGridConfigViewModel.set("selectedUserGridConfig", userMappingConfigs[0]);
        }
    }

    /**
     * Dispose the extension.
     */
    public dispose(): void {
        if (this.tooltipExtension) {
            this.tooltipExtension.dispose();
        }
        if (this.toolbar) {
            this.toolbar.destroy();
        }
    }
}
